<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate ? null : team.title" :back="listPath">
            <v-chip v-if="!isCreate && !loadingEntity" small :color="team.approved ? 'success' : 'error'" :outlined="!team.approved">
                {{ team.approved ? 'Approved' : 'Pending approval' }}
            </v-chip>
            <v-btn v-if="isShow && !isDealer" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-title class="section-title">Team</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="team.title" :rules="required" label="Title *" outlined />
                        </v-col>
                        <v-col v-if="!isDealer" cols="12" md="6">
                            <v-autocomplete
                                v-model="team.dealer"
                                :items="dealers"
                                :item-text="fullName"
                                item-value="id"
                                label="Dealer"
                                prepend-inner-icon="mdi-handshake-outline"
                                :clearable="!readOnly"
                                return-object
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="team.address" label="Address" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.trim="team.coordinates"
                                label="Coordinates"
                                placeholder="37.9838, 23.7275"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.number="team.promoSorting"
                                type="number"
                                label="Promoted position"
                                hint="Leave empty for normal ordering"
                                :clearable="!readOnly"
                                outlined
                            />
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex flex-wrap checkbox-row">
                                <v-checkbox v-model="team.available" label="Available" hide-details />
                                <v-checkbox v-if="!isDealer" v-model="team.approved" label="Approved" hide-details />
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-tabs v-model="languageTab" class="px-2">
                    <v-tab>Ελληνικά</v-tab>
                    <v-tab>English</v-tab>
                </v-tabs>
                <v-divider />
                <v-card-text>
                    <v-tabs-items v-model="languageTab">
                        <v-tab-item eager>
                            <span class="editor-label">Description</span>
                            <editor v-model="team.description" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                        <v-tab-item eager>
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model.trim="team.titleEn" label="Title in English" outlined />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model.trim="team.addressEn" label="Address in English" outlined />
                                </v-col>
                            </v-row>
                            <span class="editor-label">Description in English</span>
                            <editor v-model="team.descriptionEn" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Image</v-card-title>
                <v-card-text>
                    <image-input v-model="image" :readonly="readOnly" />
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create team' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import teamService from '@/services/team-service'
    import dealerService from '@/services/dealer-service'
    import Editor from '@tinymce/tinymce-vue'
    import formPage from '@/mixins/form-page'
    import imageUpload from '@/mixins/image-upload'
    import PageHeader from '@/components/PageHeader'
    import ImageInput from '@/components/ImageInput'
    import { editorConfig, tinymceApiKey } from '@/utils/editor'
    import { entityId, fullName, pick } from '@/utils/format'

    // TeamType clears every field missing from the payload, so always send all of them
    const TEAM_FIELDS = [
        'title', 'description', 'available', 'address', 'coordinates', 'titleEn', 'descriptionEn', 'addressEn',
        'dealer', 'approved', 'promoSorting'
    ]

    function emptyTeam() {
        return {
            title: '',
            description: '',
            available: true,
            address: '',
            coordinates: '',
            titleEn: '',
            descriptionEn: '',
            addressEn: '',
            approved: true,
            promoSorting: null,
            dealer: null
        }
    }

    export default {
        name: 'TeamForm',
        components: { Editor, PageHeader, ImageInput },
        mixins: [formPage, imageUpload],
        data: () => {
            return {
                entityLabel: 'Team',
                tinymceApiKey,
                editorInit: editorConfig({ height: 320 }),
                teamId: null,
                team: emptyTeam(),
                dealers: [],
                languageTab: 0
            }
        },
        computed: {
            isDealer() {
                return this.$store.getters.isDealer
            }
        },
        created() {
            if (!this.isDealer) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                })
            }
            if (this.isCreate) {
                return
            }
            this.teamId = parseInt(this.$route.params.teamId)
            this.loadingEntity = true
            teamService.getTeamById(this.teamId).then(resp => {
                const team = resp.data.team
                this.team = { ...emptyTeam(), ...team }
                this.setExistingImage(team.image)
            }).catch(error => {
                this.$notify.error(error, 'Could not load the team')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            fullName,
            buildPayload() {
                const payload = pick(this.team, TEAM_FIELDS)
                payload.dealer = entityId(this.team.dealer)
                payload.promoSorting = this.team.promoSorting === '' ? null : this.team.promoSorting
                return payload
            },
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                let savedId = null
                const request = this.isEdit
                    ? teamService.editTeamById(this.teamId, this.buildPayload())
                    : teamService.createTeam(this.buildPayload())
                request.then(resp => {
                    savedId = this.isEdit ? this.teamId : resp.data.id
                    return this.saveImage('teamId', savedId, imageService.uploadNewImage)
                }).then(() => {
                    this.$notify.success(this.isCreate ? 'Team created' : 'Team saved')
                    this.goBack()
                }).catch(error => {
                    if (!savedId) {
                        this.$notify.error(error, 'Could not save the team')
                        return
                    }
                    this.$notify.error('The team was saved, but the image could not be saved.')
                    if (this.isCreate) {
                        this.goToEntity(savedId, 'edit')
                    }
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>
